/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo } from 'react';
import { noop } from 'lodash';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { cssMap, jsx } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { useFlags } from '@atlaskit/flag/use-flags';
import { Box } from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import { fireUIAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { SCREEN } from '@atlassian/analytics-bridge/constants';
import { getEnv, EnvType } from '@atlassian/help-center-common-util/env';
import { ErrorBoundary } from '@atlassian/proforma-common-core/error-boundary';
import { UtilsProvider } from '@atlassian/proforma-common-core/utils-provider';
import { Environment } from '@atlassian/proforma-common-core/environments';
import { createErrorUtils } from '@atlassian/proforma-common-core/live';
import { FormRenderer } from '@atlassian/proforma-form-renderer/form-renderer';
import { FormRendererContainer } from '@atlassian/proforma-form-renderer/form-renderer-store';
import { FormRendererLifecycleStage } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import { FormFieldType } from '@atlassian/proforma-form-renderer/form-fields';
import { FormRenderMode } from '@atlassian/proforma-form-renderer/render-mode';
import { VisibilityFieldSet } from '@atlassian/proforma-form-renderer/form';
import type { FieldChangedLifecycleEvent } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import type { Renderers } from '@atlassian/proforma-form-renderer/renderers';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';
import { NoticeProvider } from '@atlassian/proforma-utilities/show-notice/main';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';
import { AnalyticsProvider } from '@atlassian/proforma-utils/analytics/provider';
import { useUFOComponentExperience } from '@atlassian/ufo/use-component-experience';

import { portalRequestCreateFormExperience } from '../../common/experiences';
import type { Forms } from '../../common/types/forms';
import {
	ProformaRequestCreateContextProvider,
	useForms,
	useJiraFieldData,
	useLocale,
	useProformaTemplateForm,
	useValidationFieldSet,
} from '../../controllers/request-create-context-provider';
import { type ProformaRequestCreateProps } from './types';
import { useRefreshForm } from './utils';
import { useAsyncConfig } from './utils/async-fields';
import { useAttachmentConfiguration } from './utils/attachment-configuration';
import { useAttachmentUploadEnd } from './utils/attachment-lifecycle-handlers';
import { useCmdbDefaults } from './utils/cmdb-defaults';
import { buildFormViewedAnalyticsAttributes } from './utils/form-viewed-analytics-attributes';
import {
	useFormikRegisterProformaForm,
	useFormikFieldErrors,
	useFormikBlockSubmissionDuringUpload,
} from './utils/formik-integration';
import { KnowledgeBaseSuggestionsContextProvider } from '../../controllers/knowledge-base-suggestions-context-provider';
import { KnowledgeBaseSuggestionFieldContainer } from './knowledge-base-suggestion-field-container';
import { useKnowledgeBaseSuggestions } from './utils/knowledge-base-suggestions';
import { useProformaFormDirty } from './utils/proforma-form-dirty';

const styles = cssMap({
	formRendererWrapper: {
		marginBlockStart: token('space.200', '1rem'),
		marginInlineEnd: token('space.negative.100', '-0.5rem'),
		marginBlockEnd: token('space.negative.100', '-0.5rem'),
		marginInlineStart: token('space.negative.100', '-0.5rem'),
	},
	spinnerBackdrop: {
		display: 'flex',
		position: 'relative',
		insetBlock: token('space.200', '1rem'),
		insetInline: 0,
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const proformaEnvironment: Record<EnvType, Environment> = {
	[EnvType.LOCAL]: Environment.LOCAL,
	[EnvType.DEV]: Environment.DEVELOPMENT,
	[EnvType.STAGING]: Environment.STAGING,
	[EnvType.PROD]: Environment.PRODUCTION,
};

interface ProformaRequestCreateInnerProps {
	onRenderComplete: () => void;
}

export const ProformaRequestCreate = ({
	blockFormSubmission,
	fieldErrors,
	knowledgeBase,
	locale,
	mediaUploadConfig,
	onRenderComplete,
	portalId,
	proformaFormIsDirty,
	proformaTemplateForm,
	projectId,
	registerProformaForm,
	reporterAccountId,
	requestTypeId,
	serviceDeskId,
	setProformaFormDirtyAction,
}: ProformaRequestCreateProps) => {
	di(useUFOComponentExperience);

	useUFOComponentExperience(portalRequestCreateFormExperience);

	return (
		<ProformaRequestCreateContextProvider
			blockFormSubmission={blockFormSubmission}
			fieldErrors={fieldErrors}
			initialMediaUploadConfig={mediaUploadConfig}
			knowledgeBase={knowledgeBase}
			locale={locale}
			portalId={portalId}
			proformaFormIsDirty={proformaFormIsDirty}
			proformaTemplateForm={proformaTemplateForm}
			projectId={projectId}
			registerProformaForm={registerProformaForm}
			reporterAccountId={reporterAccountId}
			requestTypeId={requestTypeId}
			serviceDeskId={serviceDeskId}
			setProformaFormDirtyAction={setProformaFormDirtyAction}
		>
			<FormRendererContainer>
				<FlagsProvider>
					<AnalyticsProvider>
						<AnalyticsContext
							data={{
								source: `proformaJiraPortalCreate${SCREEN}`,
								attributes: {
									serviceDeskId,
									requestTypeId,
									projectId,
									projectType: 'service_desk',
								},
							}}
						>
							<ProformaRequestCreateWithErrorHandling onRenderComplete={onRenderComplete} />
						</AnalyticsContext>
					</AnalyticsProvider>
				</FlagsProvider>
			</FormRendererContainer>
		</ProformaRequestCreateContextProvider>
	);
};

/**
 * Wrapper that provides PfErrorUtilsProvider so useRefreshForm (and its useGetFieldDataService)
 * can call usePfErrorUtils. Must wrap ProformaRequestCreateInner before any hooks that need
 * errorUtils run.
 *
 * Why this cannot live in ProformaRequestCreate: errorUtils is created from useAnalyticsEvents()
 * and useFlags(), which require the component to be inside AnalyticsContext and FlagsProvider.
 * ProformaRequestCreate renders those providers (it sits above them), so it cannot call those
 * hooks. This intermediate component lives inside the provider tree and can create errorUtils,
 * then pass it to ErrorBoundary and UtilsProvider.
 */
const ProformaRequestCreateWithErrorHandling = ({
	onRenderComplete,
}: ProformaRequestCreateInnerProps) => {
	di(createErrorUtils);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const environment = proformaEnvironment[getEnv().environment];
	const errorUtils = useMemo(
		() => createErrorUtils('PortalCreate', environment, createAnalyticsEvent, showFlag),
		[createAnalyticsEvent, environment, showFlag],
	);

	return (
		<ErrorBoundary errorUtils={errorUtils}>
			<UtilsProvider utils={{ errorUtils }}>
				<ProformaRequestCreateFormLoader onRenderComplete={onRenderComplete} />
			</UtilsProvider>
		</ErrorBoundary>
	);
};

const ProformaRequestCreateFormLoader = ({ onRenderComplete }: ProformaRequestCreateInnerProps) => {
	di(useForms, useProformaTemplateForm, useRefreshForm, Spinner);

	const forms = useForms();
	const proformaTemplateForm = useProformaTemplateForm();
	const { refreshForm, isFetchingFieldData, isFetchingFormChoices } = useRefreshForm();

	useEffect(() => {
		if (proformaTemplateForm && !forms) {
			refreshForm(proformaTemplateForm).catch(() => {
				// Error handling is done in the hook
			});
		}
	}, [proformaTemplateForm, forms, refreshForm]);

	if (!proformaTemplateForm) {
		return null;
	}

	if (isFetchingFieldData || isFetchingFormChoices || !forms) {
		return (
			<div css={styles.spinnerBackdrop} data-testid="proforma-request-create.loading">
				<Spinner size="large" />
			</div>
		);
	}

	const proformaForm = forms.proformaForm as ProformaForm;

	return (
		<ProformaRequestCreateForm
			forms={forms}
			proformaForm={proformaForm}
			onRenderComplete={onRenderComplete}
		/>
	);
};

interface ProformaRequestCreateFormProps {
	forms: Forms;
	proformaForm: ProformaForm;
	onRenderComplete: () => void;
}

/**
 * Renders the form when forms data is ready. Split out so that hooks depending on forms
 * (useJiraFieldData, useValidationFieldSet, useAsyncConfig, etc.) are only called when
 * this component is mounted, preserving the Rule of Hooks.
 */
const ProformaRequestCreateForm = ({
	forms,
	proformaForm,
	onRenderComplete,
}: ProformaRequestCreateFormProps) => {
	di(
		useJiraFieldData,
		useValidationFieldSet,
		useAsyncConfig,
		useAttachmentConfiguration,
		useAttachmentUploadEnd,
		useFormikRegisterProformaForm,
		useFormikFieldErrors,
		useFormikBlockSubmissionDuringUpload,
		useKnowledgeBaseSuggestions,
		useCmdbDefaults,
		useProformaFormDirty,
		useLocale,
		FormRenderer,
		fireUIAnalytics,
		buildFormViewedAnalyticsAttributes,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const locale = useLocale();
	const jiraFieldData = useJiraFieldData();
	const validationFieldSet = useValidationFieldSet();
	const asyncFields = useAsyncConfig(jiraFieldData, proformaForm);
	const attachmentConfiguration = useAttachmentConfiguration();
	const onAttachmentUploadEnd = useAttachmentUploadEnd();
	const {
		summaryFieldId,
		knowledgeBaseArticles,
		onFieldChanged: handleKnowledgeBaseSuggestionsOnFieldChanged,
	} = useKnowledgeBaseSuggestions(proformaForm);
	const { onFieldChanged: handleCmdbDefaultsOnFieldChanged } = useCmdbDefaults(proformaForm);
	const onProformaFormDirty = useProformaFormDirty();

	useFormikRegisterProformaForm(forms);
	useFormikFieldErrors(forms);
	useFormikBlockSubmissionDuringUpload();

	const formViewedAttributes = useMemo(
		() => buildFormViewedAnalyticsAttributes(proformaForm, locale),
		[proformaForm, locale],
	);

	const displayLocale =
		proformaForm?.design?.settings?.translatedLocale ??
		proformaForm?.design?.settings?.primaryLocale ??
		proformaForm?.design?.settings?.language ??
		locale;

	const lifecycleHandlers = useMemo(
		() => ({
			[FormRendererLifecycleStage.RENDER_COMPLETE]: () => {
				onRenderComplete();
				portalRequestCreateFormExperience.success().catch(noop);
				fireUIAnalytics(
					createAnalyticsEvent({}),
					`${ActionSubject.FORM} ${Action.VIEWED}`,
					'proformaRenderForm',
					formViewedAttributes,
				);
			},
			[FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END]: onAttachmentUploadEnd,
			[FormRendererLifecycleStage.FIELD_CHANGED]: (event: FieldChangedLifecycleEvent) => {
				// `FieldId` is typed as `string` in `@atlassian/proforma-form-renderer` (`common/types/form/form-fields/index.ts`),
				// but ADF layout stores question ids as JSON numbers in many legacy templates.
				// FormRenderer passes through ADF `parameters.id` without coercion; it may be a number at runtime while
				// `proformaForm.design.questions` keys are strings. Normalize once here so lifecycle handlers can assume string ids.
				// See ARCHITECTURE.md ("FIELD_CHANGED fieldId").
				// See platform/packages/proforma/proforma-form-renderer/src/common/utils/sections/types.tsx for callout that legacy data may have numeric ids.
				const normalizedEvent: FieldChangedLifecycleEvent = {
					...event,
					fieldId: String(event.fieldId) as FieldChangedLifecycleEvent['fieldId'],
				};
				handleKnowledgeBaseSuggestionsOnFieldChanged(normalizedEvent);
				handleCmdbDefaultsOnFieldChanged(normalizedEvent);
				onProformaFormDirty();
			},
		}),
		[
			onRenderComplete,
			onAttachmentUploadEnd,
			handleKnowledgeBaseSuggestionsOnFieldChanged,
			handleCmdbDefaultsOnFieldChanged,
			onProformaFormDirty,
			createAnalyticsEvent,
			formViewedAttributes,
		],
	);

	const renderers: Renderers = useMemo(
		() => ({
			formFieldComponentRenderers: {
				Container: {
					[FormFieldType.SHORT_TEXT]: KnowledgeBaseSuggestionFieldContainer,
					[FormFieldType.LONG_TEXT]: KnowledgeBaseSuggestionFieldContainer,
				},
			},
		}),
		[],
	);

	return (
		<Box testId="proforma-request-create">
			<NoticeProvider>
				<KnowledgeBaseSuggestionsContextProvider
					summaryFieldId={summaryFieldId}
					knowledgeBaseArticles={knowledgeBaseArticles}
				>
					<div css={styles.formRendererWrapper} data-testid="proforma-request-create.form-renderer">
						<FormRenderer
							renderMode={FormRenderMode.EDIT}
							form={forms.form}
							locale={displayLocale}
							fieldVisibility={{
								fieldSet: [VisibilityFieldSet.VISIBLE_SECTION_FIELDS],
							}}
							fieldValidation={{
								validationTargets: {
									fieldSet: validationFieldSet,
								},
							}}
							attachmentConfiguration={attachmentConfiguration}
							asyncFields={asyncFields}
							lifecycleHandlers={lifecycleHandlers}
							renderers={renderers}
						/>
					</div>
				</KnowledgeBaseSuggestionsContextProvider>
			</NoticeProvider>
		</Box>
	);
};
